import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router'
import { useIntercom } from 'react-use-intercom'

import BookImage from '@/assets/images/book.png'
import DiscountImage from '@/assets/images/discount.png'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { AppRoutes } from '@/utils/constants'
import { classNames } from '@/utils/helpers'
import { useIsDarkTheme } from '@/utils/useTheme'

interface Props {
  isOpen: boolean
  onClose: () => void
}

export const MidYearSaleDialog = ({ isOpen, onClose }: Props) => {
  const isDark = useIsDarkTheme()
  const navigate = useNavigate()
  const intercom = useIntercom()

  const handleClickUpgrade = () => {
    onClose()
    navigate(AppRoutes.Licenses.YourLicenses.asUrl())
    intercom.showNewMessage(
      'I want to claim the 50% discount mid-year offer and upgrade my license(s)',
    )
  }

  const handleClickGetLicense = () => {
    navigate(AppRoutes.Licenses.Index.asUrl())
    onClose()
    intercom.showNewMessage(
      'I want to claim the 50% discount mid-year offer and add new license/s',
    )
  }

  return (
    <>
      <Helmet>
        <link
          href="https://fonts.googleapis.com/css2?family=Lexend+Deca:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <Dialog
        show={isOpen}
        onClose={onClose}
        panelClassName="bg-cover !w-[938px] !max-w-full !p-0 overflow-visible rounded-2xl border border-secondary-100 bg-[url('/mid-year-dialog.webp')] dark:bg-[url('/mid-year-sale-dark.webp')]"
        closeIconClassName="sm:left-[calc(100%+12px)] w-10 h-10 sm:top-0 sm:border-rounded p-3 sm:bg-white sm:rounded-full flex items-center justify-center dark:bg-black dark:[&_svg]:text-white"
      >
        <div className="h-full w-full overflow-hidden rounded-2xl bg-white/40 p-4 font-lexend dark:bg-[#0A0A1D]/90 sm:px-8 sm:py-10">
          <h2 className="mx-auto mb-6 w-[80%] text-center text-2xl font-bold sm:mb-10 sm:w-full sm:text-[47px]">
            Our Exclusive Midyear Offer
          </h2>
          <div className="flex gap-4 max-sm:flex-col">
            <div className="rounded-xl bg-[linear-gradient(90deg,#572CD4_0%,#2080E2_100%)] p-6 text-white">
              <div className="flex h-full flex-col justify-between gap-6">
                <div>
                  <h3 className="mb-6 text-[32px] font-bold">
                    Claim our Midyear Offer today
                  </h3>
                  <p className="text-base">
                    Upgrade or purchase new licenses and receive access to our
                    limited offer
                  </p>
                </div>
                <div className="flex flex-col items-stretch gap-3">
                  <Button
                    className="bg-white hover:bg-white/90"
                    onClick={handleClickGetLicense}
                  >
                    <span className="bg-[linear-gradient(90deg,#282B61_0%,#3846A7_24.78%,#292D65_43.41%,#36439F_69.47%,#2D3477_100%)] bg-clip-text text-transparent">
                      New License
                    </span>
                  </Button>
                  <Button
                    className="border border-secondary-100 bg-transparent shadow-none hover:bg-secondary-600/10"
                    onClick={handleClickUpgrade}
                  >
                    Upgrade License
                  </Button>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <div
                className={classNames(
                  'rounded-2xl bg-card p-6',
                  isDark &&
                    'border border-secondary-100 bg-black/5 backdrop-blur-xl',
                )}
              >
                <img
                  src={DiscountImage}
                  alt="discount"
                  className="mb-4 h-[80px] w-[80px]"
                />
                <p className="mb-3 text-[20px] font-semibold">50% Discount</p>
                <p>
                  Receive a 50% Discount on all Upgrades and new additional
                  Licenses for a limited time only. The discount will only be
                  applied to the first month.
                </p>
              </div>
              <div
                className={classNames(
                  'rounded-2xl bg-card p-6',
                  isDark &&
                    'border border-secondary-100 bg-black/5 backdrop-blur-xl',
                )}
              >
                <img
                  src={BookImage}
                  alt="blueprint"
                  className="mb-4 h-[80px] w-[80px]"
                />
                <p className="mb-3 text-[20px] font-semibold">
                  “The Blueprint” E-Book
                </p>
                <p>
                  Receive our Blueprint E-Book to get insider knowledge that
                  will bring your agency to the next level.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  )
}
